import React from 'react';
import { t, Trans } from '@lingui/macro';
import styled from 'styled-components';
import {
    Nav,
    Progress,
    Box,
    Badge,
    fontWeight,
    spacing,
    palette,
    radius,
    BadgeVariant,
    Level,
    Text,
    useBreakpoint,
} from '@landr/maestro';
import { useUserCreditsContext } from 'contexts/UserCreditsContext/UserCreditsContext';
import { usePricingConfig } from 'hooks/usePricingConfig';
import { getPricingLinkTarget } from 'helpers';
import isNumber from 'lodash/isNumber';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { RouterLink } from 'components/CommonUi/RouterLink';
import { CONFIG } from 'utils/config';

const StyledBadge = styled(Badge)`
    font-weight: ${fontWeight('bold')};
    cursor: pointer;
    height: ${spacing('lg')};
    vertical-align: middle;
`;

const StyledLoadingBadge = styled(Box)`
    display: inline-flex;
    height: ${spacing('lg')};
    background-color: ${palette('primary.500')};
    vertical-align: middle;
    border-radius: ${radius('round')};
    text-align: center;
    padding: 4.5px 8px;
`;

const LoadingBadge = () => (
    <StyledLoadingBadge>
        <Progress size={11} />
    </StyledLoadingBadge>
);

const useBalanceBadge = ({
    isPlanPaused,
    isPoorStanding,
    userCredits,
    authenticatedPricingPath,
}: {
    isPlanPaused: boolean;
    isPoorStanding: boolean;
    userCredits?: number;
    authenticatedPricingPath: string;
}) => {
    let to = authenticatedPricingPath;
    let badgeVariant: BadgeVariant = 'primary';

    if (isPoorStanding) {
        to = CONFIG.VITE_ACCOUNT_BILLING_URL;
        badgeVariant = 'danger';
    } else if (isPlanPaused) {
        to = CONFIG.VITE_ACCOUNT_PLANS_URL;
        badgeVariant = 'warning';
    }

    const content = isNumber(userCredits)
        ? t({
              comment: '"cr." is the abbreviation of "credit"',
              message: `${userCredits?.toString(10)} cr.`,
          })
        : undefined;

    return { badgeVariant, content, to };
};

const RemainingCredits = ({ onClick }: { onClick: () => void }) => {
    const isMobile = useBreakpoint('md');
    const { authenticatedPricingPath } = usePricingConfig();
    const target = getPricingLinkTarget();
    const { userCredits } = useUserCreditsContext();

    const { isPlanPaused, isPoorStanding } =
        useSubscriptionsAndAllowedFeaturesContext();

    const { badgeVariant, content, to } = useBalanceBadge({
        isPlanPaused,
        isPoorStanding,
        userCredits,
        authenticatedPricingPath,
    });

    return (
        <Nav.Link as={RouterLink} to={to} target={target} onClick={onClick}>
            <Level>
                {isMobile && (
                    <Level.Item>
                        <Text size="sm">
                            <Trans>Credit balance available</Trans>
                        </Text>
                    </Level.Item>
                )}
                <Level.Item>
                    {content ? (
                        <StyledBadge
                            aria-label="User Credit Count"
                            size="md"
                            kind="subtle"
                            variant={badgeVariant}
                            isRounded
                            showZero={true}
                            max={9999}
                            content={content}
                        />
                    ) : (
                        <LoadingBadge />
                    )}
                </Level.Item>
            </Level>
        </Nav.Link>
    );
};

export default RemainingCredits;
